// App-wide upload queue toast (Figma Web / Uploads / Queue toast 4332:1959, screen 4334:2499): shown
// while any wall video or chat album ("Sending N items") is sending (switched conversation, closed the chat or the post editor), with the
// keep-tab-open hint; a failed video that can still be retried shows Retry. Leaving the tab also
// triggers the browser's own beforeunload prompt (store/uploadQueue).
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { AlertCircle, Clock, Video } from "lucide-react";
import { canRetry, isActive, uploadQueue, useUploadQueueStore, type UploadItem } from "../../store/uploadQueue";
import { dismissWallPostNotice, useWallVideoPostStore } from "../../services/wallVideoPost";
import { toMb } from "../../utils/chatMedia";

const pct = (p: number) => Math.max(0, Math.min(100, Math.round(p * 100)));

function ProgressBar({ percent, label }: { percent: number; label: string }) {
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
      style={{ position: "relative", width: "100%", height: 3, background: "var(--bg-hover)", flexShrink: 0 }}
    >
      <div style={{ position: "absolute", inset: 0, width: `${percent}%`, background: "var(--primary)" }} />
    </div>
  );
}

const TITLE: React.CSSProperties = { fontSize: 14, fontWeight: 700, lineHeight: 1, color: "var(--foreground)", whiteSpace: "nowrap" };
const DETAIL: React.CSSProperties = { fontSize: 12, lineHeight: 1.3, color: "var(--foreground-muted)" };
const HINT: React.CSSProperties = { fontSize: 12, lineHeight: 1.3, color: "var(--foreground-subtle)" };
const ACTION: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 700,
  lineHeight: 1,
  color: "var(--primary-text)",
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",
  whiteSpace: "nowrap",
};

export default function UploadQueueToast() {
  const { t } = useTranslation();
  const items = useUploadQueueStore((s) => s.items);
  const order = useUploadQueueStore((s) => s.order);
  const list = order.map((id) => items[id]).filter((i): i is UploadItem => !!i);
  const active = list.filter(isActive);
  const failed = active.length === 0 ? list.find((i) => canRetry(i)) : undefined;
  // A post queued from the closed editor (Figma state "Post queued"), and one dropped ("Post not published").
  const queued = useWallVideoPostStore((s) => s.queued);
  const notice = useWallVideoPostStore((s) => s.notice);
  const postIds = new Set(
    Object.values(queued).flatMap((p) => p.items.flatMap((it) => (it.kind === "file" && it.uploadId != null ? [it.uploadId] : []))),
  );
  const postQueued = Object.keys(queued).length > 0 && (active.length === 0 || active.every((i) => postIds.has(i.id)));

  // The batch "Uploading i of n" counts: every item seen active since the toast last showed.
  const activeKey = active.map((i) => i.id).join(",");
  const [batch, setBatch] = useState<string[]>([]);
  useEffect(() => {
    const ids = activeKey ? activeKey.split(",") : [];
    setBatch((cur) => {
      if (ids.length === 0) return cur.length === 0 ? cur : [];
      const next = [...cur, ...ids.filter((id) => !cur.includes(id))];
      return next.length === cur.length ? cur : next;
    });
  }, [activeKey]);

  if (active.length === 0 && !failed && !notice && !postQueued) return null;

  let icon = <Video size={20} color="var(--primary-text)" />;
  let title: string;
  let detail: string;
  let percent: number | null = null;
  let action: { label: string; run(): void } | null;

  if (notice) {
    icon = <AlertCircle size={20} color="var(--destructive)" />;
    title = t("upload_queue_post_not_published");
    detail = notice.failedName ? t("upload_queue_failed_detail", { name: notice.failedName }) : "";
    action = { label: t("wall_close"), run: dismissWallPostNotice };
  } else if (postQueued) {
    // The post's own videos: done ones count whole, the uploading one by its bytes.
    const mine = list.filter((i) => postIds.has(i.id));
    const sum = mine.reduce((acc, i) => acc + (i.phase === "done" ? 1 : i.phase === "uploading" ? i.progress : 0), 0);
    icon = <Clock size={20} color="var(--primary-text)" />;
    percent = mine.length > 0 ? pct(sum / mine.length) : 100;
    title = t("upload_queue_post_queued");
    detail = `${t("upload_queue_post_queued_detail")} · ${percent}%`;
    action = null;
  } else if (failed) {
    icon = <AlertCircle size={20} color="var(--destructive)" />;
    // A chat album (photos and/or videos) says "Not sent"; a wall video "Video not sent".
    title = failed.slots ? t("chat_album_not_sent") : t("upload_queue_not_sent");
    const failedName = failed.slots?.find((sl) => sl.phase === "error")?.fileName ?? failed.fileName;
    detail = t("upload_queue_failed_detail", { name: failedName });
    action = { label: t("upload_queue_retry"), run: () => uploadQueue.retry(failed.id) };
  } else if (active.length === 1) {
    const it = active[0];
    // A chat album: "Sending N items" (Figma chat_album_notification_sending); a wall video as before.
    title = it.slots ? t("chat_album_notification_sending", { count: it.slots.length }) : t("upload_queue_sending_video");
    percent = it.phase === "waiting" ? 0 : pct(it.progress);
    detail =
      it.phase === "uploading"
        ? `${t("chat_video_uploading", { percent })} · ${t("chat_video_upload_size", { sent: toMb(it.bytesSent), total: toMb(it.bytesTotal) })}`
        : `${t("chat_video_compressing", { percent })} · ${it.fileName}`;
    action = { label: t("video_upload_cancel"), run: () => uploadQueue.cancel(it.id) };
  } else {
    // Items sent (and removed) since the batch began still count as finished.
    const seen = new Set([...batch, ...active.map((i) => i.id)]);
    const total = seen.size;
    const finished = total - active.length;
    const running = active.find((i) => i.phase !== "waiting");
    percent = pct((finished + (running?.progress ?? 0)) / total);
    // Albums count their items; with any album in the batch the copy is about items, not videos.
    title = active.some((i) => i.slots)
      ? t("chat_album_notification_sending", {
          count: active.reduce((n, i) => n + (i.slots?.length ?? 1), 0),
        })
      : t("upload_queue_sending_count", { n: active.length });
    detail = t("upload_queue_uploading_n_of", { n: finished + 1, total, percent });
    action = {
      label: t("video_upload_cancel_all"),
      run: () => active.forEach((i) => uploadQueue.cancel(i.id)),
    };
  }

  return createPortal(
    <div
      data-testid="upload-queue-toast"
      role="status"
      // Bottom-left above the composer area, clear of the desktop nav rail.
      className="left-4 md:left-[88px]"
      style={{
        position: "fixed",
        bottom: 88,
        zIndex: 2000,
        width: 340,
        maxWidth: "calc(100vw - 32px)",
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: 12,
        borderRadius: 12,
        background: "var(--bg-surface)",
        boxShadow: "0 4px 14px rgba(15,23,42,0.18)",
      }}
    >
      <span style={{ display: "flex", flexShrink: 0 }}>{icon}</span>
      <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 0 }}>
        <span style={TITLE}>{title}</span>
        {detail && <span style={DETAIL}>{detail}</span>}
        {percent !== null && (
          <>
            <ProgressBar percent={percent} label={detail} />
            <span style={HINT}>{t("upload_queue_keep_tab_open")}</span>
          </>
        )}
      </div>
      {action && (
        <button type="button" style={ACTION} onClick={action.run}>
          {action.label}
        </button>
      )}
    </div>,
    document.body,
  );
}
